import {
    Box,
    Flex,
    Text,
    useColorModeValue,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import React, { useRef } from "react";
import { Building2, ShoppingBag } from "lucide-react";
import { motion, useInView } from "framer-motion";

const MotionBox = motion(Box);

interface ActivityItem {
    id: string;
    icon: React.ReactNode;
    title: string;
    description: string | string[];
    accent: string;
    num: string;
    enterFrom: "left" | "right";
}

// ─── Carte activité ────────────────────────────────────────────────────────────
const ActivityCard: React.FC<{
    act: ActivityItem;
    index: number;
    isInView: boolean;
    textColor: string;
    cardBg: string;
    cardBorder: string;
}> = ({ act, index, isInView, textColor, cardBg, cardBorder }) => {

    const cardVariants = {
        hidden: {
            opacity: 0,
            x: act.enterFrom === "left" ? -100 : 100,
            y: 24,
            scale: 0.95,
            filter: "blur(8px)",
        },
        visible: {
            opacity: 1,
            x: 0,
            y: 0,
            scale: 1,
            filter: "blur(0px)",
            transition: {
                duration: 1.1,
                delay: index * 0.15,
                ease: [0.16, 1, 0.3, 1],
            },
        },
        exit: {
            opacity: 0,
            x: act.enterFrom === "left" ? -60 : 60,
            scale: 0.97,
            filter: "blur(6px)",
            transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
        },
    };

    const listItemVariants = {
        hidden: { opacity: 0, x: -14 },
        visible: (i: number) => ({
            opacity: 1,
            x: 0,
            transition: { duration: 0.5, delay: 0.6 + i * 0.09, ease: [0.16, 1, 0.3, 1] },
        }),
    };

    return (
        <MotionBox
            variants={cardVariants}
            initial="hidden"
            animate={isInView ? "visible" : "exit"}
            whileHover={{
                y: -12,
                scale: 1.012,
                transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] },
            }}
            style={{ borderRadius: "28px", height: "100%", perspective: "1000px" }}
        >
            <Box
                h="100%"
                p={{ base: 10, md: 14 }}
                bg={cardBg}
                border="1px solid"
                borderColor={cardBorder}
                borderRadius="28px"
                backdropFilter="blur(24px) saturate(180%)"
                boxShadow="0 1px 0 rgba(255,255,255,0.55) inset, 0 40px 80px rgba(0,0,0,0.07)"
                position="relative"
                overflow="hidden"
                display="flex"
                flexDirection="column"
            >
                {/* Numéro décoratif en fond */}
                <Box
                    position="absolute"
                    right="-12px"
                    bottom="-20px"
                    pointerEvents="none"
                    userSelect="none"
                >
                    <Text
                        fontSize="140px"
                        fontWeight="900"
                        lineHeight="1"
                        letterSpacing="-0.05em"
                        color={`${act.accent}09`}
                    >
                        {act.num}
                    </Text>
                </Box>

                {/* Ligne accent haut */}
                <Box
                    position="absolute"
                    top={0}
                    left="10%"
                    right="10%"
                    h="1px"
                    bg={`linear-gradient(90deg, transparent, ${act.accent}90, transparent)`}
                />

                {/* Halo icône */}
                <Box
                    position="absolute"
                    top="-30px"
                    left="48px"
                    w="100px"
                    h="100px"
                    borderRadius="full"
                    bg={`radial-gradient(ellipse, ${act.accent}14 0%, transparent 70%)`}
                    pointerEvents="none"
                />

                {/* Icône */}
                <motion.div
                    initial={{ scale: 0.7, opacity: 0, y: 10 }}
                    animate={isInView
                        ? { scale: 1, opacity: 1, y: 0 }
                        : { scale: 0.7, opacity: 0, y: 10 }
                    }
                    transition={{ duration: 0.7, delay: index * 0.15 + 0.35, ease: [0.16, 1, 0.3, 1] }}
                >
                    <Box
                        color={act.accent}
                        mb={8}
                        p={4}
                        borderRadius="18px"
                        border="1px solid"
                        borderColor={`${act.accent}28`}
                        bg={`${act.accent}08`}
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        {act.icon}
                    </Box>
                </motion.div>

                {/* Titre */}
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }}
                    transition={{ duration: 0.6, delay: index * 0.15 + 0.45, ease: [0.16, 1, 0.3, 1] }}
                >
                    <Text
                        fontSize={{ base: "xl", md: "2xl" }}
                        fontWeight="300"
                        letterSpacing="0.12em"
                        textTransform="uppercase"
                        color={textColor}
                        mb={5}
                    >
                        {act.title}
                    </Text>
                </motion.div>

                {/* Séparateur */}
                <motion.div
                    initial={{ scaleX: 0 }}
                    animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.15 + 0.52, ease: [0.16, 1, 0.3, 1] }}
                    style={{ originX: 0, marginBottom: "1.5rem" }}
                >
                    <Box w="36px" h="1px" bg={act.accent} opacity={0.6} />
                </motion.div>

                {/* Description */}
                <Box flex="1">
                    {Array.isArray(act.description) ? (
                        <Flex direction="column" gap={0}>
                            {act.description.map((line, idx) => (
                                <MotionBox
                                    key={idx}
                                    variants={listItemVariants}
                                    initial="hidden"
                                    animate={isInView ? "visible" : "hidden"}
                                    custom={idx}
                                >
                                    <Flex align="flex-start" gap={3} py={1}>
                                        {/* Point accent */}
                                        <Box
                                            mt="11px"
                                            w="4px"
                                            h="4px"
                                            borderRadius="full"
                                            bg={act.accent}
                                            opacity={0.6}
                                            flexShrink={0}
                                        />
                                        <Text
                                            fontSize={{ base: "md", md: "lg" }}
                                            color={textColor}
                                            opacity={0.65}
                                            lineHeight="1.9"
                                            fontWeight="300"
                                        >
                                            {line}
                                        </Text>
                                    </Flex>
                                </MotionBox>
                            ))}
                        </Flex>
                    ) : (
                        <Text
                            fontSize={{ base: "md", md: "lg" }}
                            color={textColor}
                            opacity={0.65}
                            lineHeight="1.9"
                            fontWeight="300"
                        >
                            {act.description}
                        </Text>
                    )}
                </Box>
            </Box>
        </MotionBox>
    );
};

// ─── Composant principal ───────────────────────────────────────────────────────
const Activity: React.FC = () => {
    const { t } = useTranslation();
    const ref = useRef(null);
    const isInView = useInView(ref, { once: false, margin: "-100px" });

    const bg        = useColorModeValue("#F4F3EF", "#0D1117");
    const textColor = useColorModeValue("#0A1628", "#E8E0D0");
    const cardBg    = useColorModeValue("rgba(255,255,255,0.62)", "rgba(255,255,255,0.04)");
    const cardBorder = useColorModeValue("rgba(10,22,40,0.09)", "rgba(255,255,255,0.07)");

    const activities: ActivityItem[] = [
        {
            id: "construction",
            icon: <Building2 size={40} strokeWidth={1} />,
            title: t("activitiTitrecons"),
            description: [
                t("activitiTextecons"),
                t("activitiTextecons1"),
                t("activitiTextecons2"),
                t("activitiTextecons3"),
            ],
            accent: "#C9A84C",
            num: "01",
            enterFrom: "left",
        },
        {
            id: "boutique",
            icon: <ShoppingBag size={40} strokeWidth={1} />,
            title: t("activitiTitrevent"),
            description: t("activitiTextevent"),
            accent: "#8B9E6E",
            num: "02",
            enterFrom: "right",
        },
    ];

    return (
        <Flex
            id="Services"
            ref={ref}
            direction="column"
            align="center"
            justify="center"
            py={{ base: 24, md: 40 }}
            px={{ base: 6, md: 12 }}
            bg={bg}
            position="relative"
            overflow="hidden"
        >
            {/* Texture de fond subtile */}
            <Box
                position="absolute"
                inset={0}
                opacity={0.025}
                bgImage="repeating-linear-gradient(45deg, currentColor 0, currentColor 1px, transparent 0, transparent 50%)"
                bgSize="24px 24px"
                color={textColor}
                pointerEvents="none"
            />

            {/* Ambient glow */}
            <MotionBox
                position="absolute"
                top="-100px"
                right="-100px"
                w="500px"
                h="500px"
                bg={useColorModeValue(
                    "radial-gradient(ellipse, rgba(139,158,110,0.07) 0%, transparent 65%)",
                    "radial-gradient(ellipse, rgba(139,158,110,0.04) 0%, transparent 65%)"
                )}
                pointerEvents="none"
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                transition={{ duration: 1.5 }}
            />

            {/* Numéro déco fond */}
            <MotionBox
                position="absolute"
                left="-10px"
                top="50%"
                transform="translateY(-50%)"
                pointerEvents="none"
                userSelect="none"
                initial={{ opacity: 0, x: -50 }}
                animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
                transition={{ duration: 1.2, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            >
                <Text
                    fontSize={{ base: "120px", md: "200px" }}
                    fontWeight="900"
                    lineHeight="1"
                    color={useColorModeValue("rgba(10,22,40,0.04)", "rgba(255,255,255,0.025)")}
                    letterSpacing="-0.05em"
                >
                    03
                </Text>
            </MotionBox>

            {/* ── Header ── */}
            <Box textAlign="center" mb={20} position="relative" zIndex={1}>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                >
                    <Text
                        fontSize="10px"
                        letterSpacing="0.4em"
                        textTransform="uppercase"
                        color={textColor}
                        opacity={0.35}
                        fontWeight="500"
                        mb={5}
                    >
                        {t("activitiTitre")}
                    </Text>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 28 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 28 }}
                    transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                >
                    <Text
                        fontSize={{ base: "4xl", md: "6xl", lg: "7xl" }}
                        textAlign="center"
                        fontWeight="200"
                        letterSpacing="-0.04em"
                        color={textColor}
                        lineHeight="1.05"
                        mb={6}
                    >
                        {t("activitiHeading1")}{" "}
                        <Box as="span" fontWeight="800">{t("activitiHeading2")}</Box>
                    </Text>
                </motion.div>

                {/* Séparateur animé */}
                <motion.div
                    initial={{ scaleX: 0, opacity: 0 }}
                    animate={isInView ? { scaleX: 1, opacity: 1 } : { scaleX: 0, opacity: 0 }}
                    transition={{ duration: 0.8, delay: 0.22, ease: [0.16, 1, 0.3, 1] }}
                    style={{ originX: 0.5 }}
                >
                    <Box
                        w="56px"
                        h="1px"
                        bg="linear-gradient(90deg, #C9A84C, #8B9E6E)"
                        mx="auto"
                        opacity={0.7}
                    />
                </motion.div>
            </Box>

            {/* ── Cards ── */}
            <Flex
                direction={{ base: "column", md: "row" }}
                gap={8}
                maxW="1100px"
                w="100%"
                align="stretch"
                position="relative"
                zIndex={1}
            >
                {activities.map((act, i) => (
                    <Box key={act.id} flex="1" display="flex">
                        <ActivityCard
                            act={act}
                            index={i}
                            isInView={isInView}
                            textColor={textColor}
                            cardBg={cardBg}
                            cardBorder={cardBorder}
                        />
                    </Box>
                ))}
            </Flex>
        </Flex>
    );
};

export default Activity;